import React, { useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Box, Typography, AvatarGroup, Avatar } from '@mui/material'
import { IconUsers, IconBuildingStore } from '@tabler/icons-react'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { useAppSelector } from '../../../../store'
import {
  fetchGroups,
  selectGroups,
  selectGroupRequests,
  selectIsLoading,
} from '../../../../store/socialSlice'
import { selectAccessToken } from '../../../../store/authSlice'
import { memberDisplayName } from '../../../../helpers/guest-helper'
import { ROUTES } from '../../../../helpers/routes-helper'
import { Group } from '../../../../types/groupTypes'
import {
  SocialGroupGroupRequestsContainer,
  SocialGroupInnerContainer,
  SocialGroupLeftContainer,
  SocialGroupTitleContainer,
  SocialGroupTitleTypography,
  SocialGroupUserTitle,
  SocialGroupUserCountTypography,
  SocialGroupIconPointFilled,
  SocialGroupMemberListTypography,
  SocialGroupBadge,
  SocialGroupsRightContainer,
  SocialGroupDivider,
} from '../../social/styled/SocialGroup.styled'

const GroupsConversationsSection: React.FC = () => {
  const { theme, navigate, dispatch, isSmallScreen } = useAppUtilities()
  const { isAuthenticated } = useAuth0()
  const groups = useAppSelector(selectGroups)
  const groupRequests = useAppSelector(selectGroupRequests)
  const isLoading = useAppSelector(selectIsLoading)
  const accessToken = useAppSelector(selectAccessToken)

  useEffect(() => {
    // Persistent groups are an authenticated-only feature — never fetch for anon users (the
    // /groups list is JWT-scoped and would 401). This guards against future un-gated mounts;
    // Home already renders this only when authenticated.
    // Also gate on the bearer token being present: on a just-completed login, isAuthenticated
    // flips true before BaseLayout dispatches the token, so an eager fetch would race the
    // axios auth interceptor and 401/403. Re-run once the token lands.
    if (
      isAuthenticated &&
      accessToken &&
      groups.length === 0 &&
      groupRequests.length === 0 &&
      !isLoading
    ) {
      void dispatch(fetchGroups(0))
    }
  }, [isAuthenticated, accessToken])

  // Get recent groups (max 3) sorted by unread count
  const recentGroups = [...groups]
    .sort((a, b) => (b.unreadMessageCount ?? 0) - (a.unreadMessageCount ?? 0))
    .slice(0, 3)

  const hasInvites = groupRequests.length > 0

  const handleGroupClick = (groupId: string): void => {
    navigate(`${ROUTES.SOCIAL}/${groupId}`)
  }

  const handleViewAllClick = (): void => {
    navigate(ROUTES.SOCIAL)
  }

  // Anon users have no persistent groups — their entry point is the "Plan a group dinner" CTA.
  if (!isAuthenticated) {
    return null
  }

  if (isLoading && groups.length === 0) {
    return null
  }

  // Don't show if no groups and no invites
  if (recentGroups.length === 0 && !hasInvites) {
    return null
  }

  return (
    <Box
      sx={{
        marginTop: '16px',
        marginBottom: '16px',
      }}
    >
      {/* Section Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '12px',
          paddingX: '12px',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: theme.palette.grey[900],
            fontSize: '18px',
          }}
        >
          Your groups
        </Typography>
        <Typography
          variant="body2"
          onClick={handleViewAllClick}
          sx={{
            color: theme.palette.primary.main,
            fontWeight: 600,
            cursor: 'pointer',
            '&:active': {
              opacity: 0.7,
            },
          }}
        >
          View all
        </Typography>
      </Box>

      {/* Groups List using existing Social layout */}
      <Box>
        {recentGroups.map((group: Group) => (
          <SocialGroupGroupRequestsContainer
            key={group.id}
            onClick={() => handleGroupClick(group.id as string)}
          >
            <SocialGroupInnerContainer>
              <SocialGroupLeftContainer>
                <SocialGroupTitleContainer>
                  <SocialGroupTitleTypography>
                    {group.name}
                  </SocialGroupTitleTypography>
                  <SocialGroupBadge badgeContent={group.unreadMessageCount} />
                </SocialGroupTitleContainer>
                <SocialGroupUserTitle>
                  <IconUsers size={18} stroke={2} />
                  <SocialGroupUserCountTypography>
                    {group?.members?.length}
                  </SocialGroupUserCountTypography>
                  <SocialGroupIconPointFilled />
                  <SocialGroupMemberListTypography>
                    {group?.members
                      ?.slice(0, isSmallScreen ? 2 : 5)
                      ?.map((member) =>
                        memberDisplayName(member.name, member.email, 'Member'),
                      )
                      .join(', ')}
                  </SocialGroupMemberListTypography>
                </SocialGroupUserTitle>
              </SocialGroupLeftContainer>
              <SocialGroupsRightContainer>
                {!isSmallScreen && (
                  <AvatarGroup max={5} spacing={13}>
                    {group?.members?.map((_, index) => (
                      <Avatar
                        key={index}
                        alt={`group-profile-${index}`}
                        src={
                          'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQmJUeQCIV5gK-gudX5l3OIhRcmgnbtGDhExw&s'
                        }
                      />
                    ))}
                  </AvatarGroup>
                )}
              </SocialGroupsRightContainer>
            </SocialGroupInnerContainer>
            <SocialGroupDivider />
          </SocialGroupGroupRequestsContainer>
        ))}

        {/* Conversations Item using same layout */}
        <SocialGroupGroupRequestsContainer onClick={handleViewAllClick}>
          <SocialGroupInnerContainer>
            <SocialGroupLeftContainer>
              <SocialGroupTitleContainer>
                <SocialGroupTitleTypography>
                  Conversations
                </SocialGroupTitleTypography>
              </SocialGroupTitleContainer>
              <SocialGroupUserTitle>
                <IconBuildingStore size={18} stroke={2} />
                <SocialGroupMemberListTypography>
                  Chat with restaurants
                </SocialGroupMemberListTypography>
              </SocialGroupUserTitle>
            </SocialGroupLeftContainer>
          </SocialGroupInnerContainer>
          <SocialGroupDivider />
        </SocialGroupGroupRequestsContainer>
      </Box>
    </Box>
  )
}

export default GroupsConversationsSection
